import { Controller, useForm } from "react-hook-form";
import { FilterLayout } from "../layout/FilterLayout";
import { Autocomplete, FormControl, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Button from "../ui/Button";
import { formatDateToYYYYMMDD } from "../../Helper/functionHelper";
import useAuthUser from "../../hooks/useAuthUser";
import { useEffect, useState } from "react";
import { fetchDepartments } from "../../services/exitManagement";

const dayOptions = [
  { label: "Full Day", value: "FD" },
  { label: "Half Day First", value: "HDF" },
  { label: "Half Day Second", value: "HDS" },
];

const wfhTypeOptions = [
  { label: "Pending", value: "0" },
  { label: "Approved", value: "1" },
  { label: "Rejected", value: "2" },
  { label: "Cancelled", value: "3" },
];

const userStatusOptions = [
  { label: "Active", value: "1" },
  { label: "Inactive", value: "0" },
];

const Filter = ({ onFilter, exportWfh }) => {
  const { isAdminHrRole, isManagerRole } = useAuthUser();
  const [departments, setDepartments] = useState([]);
  const { control, handleSubmit, reset, getValues, watch } = useForm({
    defaultValues: {
      keywords: "",
      wfh_type: "",
      from_date: "",
      to_date: "",
      wfh_status: "",
      user_status: "1",
      department: null,
    },
  });

  const fromDate = watch("from_date");
  const toDate = watch("to_date");

  const onSubmit = (data) => {
    const searchParams = {};

    if (data?.keywords) searchParams.keywords = data.keywords;
    if (data?.wfh_type) searchParams["wfh_type"] = data.wfh_type;
    if (data?.from_date)
      searchParams["from_date"] = formatDateToYYYYMMDD(data.from_date);
    if (data?.to_date)
      searchParams["to_date"] = formatDateToYYYYMMDD(data.to_date);

    if (data?.wfh_status) searchParams.wfh_status = data.wfh_status;
    if (data?.user_status) searchParams.user_status = data.user_status;
    if (data?.department?._DeptID) {
      searchParams.department = data.department._DeptID;
    }
    onFilter(searchParams);
  };

  const handleClear = () => {
    const searchParams = {};
    searchParams.user_status = 1;
    reset();
    onFilter(searchParams);
  };
  const handleExport = () => {
    const searchParams = {};
    const formValues = getValues();

    if (formValues?.keywords) searchParams.keywords = formValues.keywords;
    if (formValues?.wfh_type) searchParams["wfh_type"] = formValues.wfh_type;
    if (formValues?.from_date)
      searchParams["from_date"] = formatDateToYYYYMMDD(formValues.from_date);
    if (formValues?.to_date)
      searchParams["to_date"] = formatDateToYYYYMMDD(formValues.to_date);

    if (formValues?.wfh_status) searchParams.wfh_status = formValues.wfh_status;
    if (formValues?.user_status)
      searchParams.user_status = formValues.user_status;
    if (formValues?.department?._DeptID) {
      searchParams.department = formValues.department._DeptID;
    }
    searchParams.export = true;
    exportWfh(searchParams);
  };

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const deptRes = await fetchDepartments();
        if (deptRes?.data) setDepartments(deptRes.data.data);
      } catch (error) {
        setDepartments([]);
        console.error("Error loading filter options:", error);
      }
    };

    if (isAdminHrRole || isManagerRole) {
      loadFilterOptions();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <FilterLayout>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          {/* Text Input */}
          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="keywords"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Keywords"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    backgroundColor: "white",
                    marginTop: "8px",
                    marginBottom: "8px",
                    height: "40px",
                    ".MuiInputBase-input": { padding: "10px 6px" },
                  }}
                />
              )}
            />
          </div>

          {/* From Date DatePicker */}
          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="from_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="From Date"
                  {...field}
                  onChange={field.onChange}
                  value={field.value || null}
                  maxDate={toDate}
                  format="dd MMM yyyy"
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true,
                      size: "small",
                      sx: {
                        backgroundColor: "white",
                        marginTop: "8px",
                        marginBottom: "8px",
                        height: "40px",
                      },
                    },
                  }}
                />
              )}
            />
          </div>

          {/* To Date DatePicker */}
          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="to_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="To Date"
                  {...field}
                  onChange={field.onChange}
                  value={field.value || null}
                  format="dd MMM yyyy"
                  minDate={fromDate}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true,
                      size: "small",
                      sx: {
                        backgroundColor: "white",
                        marginTop: "8px",
                        marginBottom: "8px",
                        height: "40px",
                      },
                    },
                  }}
                />
              )}
            />
          </div>

          {/* wfh type Select */}
          <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
            <Controller
              name="wfh_type"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    marginTop: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Autocomplete
                    {...field}
                    options={dayOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    value={
                      dayOptions.find((item) => item.value === field.value) ||
                      null
                    }
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.value : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="WFH Type"
                        size="small"
                        sx={{
                          "& .MuiInputBase-root": {
                            height: 40,
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              )}
            />
          </div>

          {/* Status Select */}
          <div className="col-lg-3 col-md-4 col-sm-6 col-xs-6">
            <Controller
              name="wfh_status"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    marginTop: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Autocomplete
                    {...field}
                    options={wfhTypeOptions}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    value={
                      wfhTypeOptions.find(
                        (item) => item.value === field.value
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      field.onChange(newValue ? newValue.value : "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Status"
                        size="small"
                        sx={{
                          "& .MuiInputBase-root": {
                            height: 40,
                            padding: 0,
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "8px 14px",
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              )}
            />
          </div>

          {(isAdminHrRole || isManagerRole) && (
            <>
              <div className="col-lg-3 col-md-4 col-sm-6 col-xs-12">
                <Controller
                  name="department"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        backgroundColor: "white",
                        marginTop: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <Autocomplete
                        {...field}
                        options={departments}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option._DeptName
                        }
                        isOptionEqualToValue={(option, value) =>
                          option._DeptID === value?._DeptID
                        }
                        value={field.value || null}
                        onChange={(event, newValue) => {
                          field.onChange(newValue || null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Department"
                            size="small"
                            sx={{
                              "& .MuiInputBase-root": {
                                height: 40,
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  )}
                />
              </div>
              <div className="col-lg-3 col-md-3 col-sm-6 col-xs-6">
                <Controller
                  name="user_status"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        backgroundColor: "white",
                        marginTop: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <Autocomplete
                        {...field}
                        options={userStatusOptions}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.label
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.value === value?.value
                        }
                        value={
                          userStatusOptions.find(
                            (item) => item.value === field.value
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          field.onChange(newValue ? newValue.value : "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Employee Status"
                            size="small"
                            sx={{
                              "& .MuiInputBase-root": {
                                height: 40,
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  )}
                />
              </div>
            </>
          )}

          {/* Filter and Clear Buttons */}
          <div className="col-lg-3 col-md-4 col-sm-12 col-xs-12 mt-2">
            <Button type="submit" variant="success">
              Filter
            </Button>
            <Button
              type="button"
              variant="dark"
              onClick={handleClear}
              style={{ marginLeft: "5px" }}
            >
              Clear
            </Button>
            {(isAdminHrRole || isManagerRole) && (
              <Button
                type="button"
                variant="dark"
                style={{ marginLeft: "5px" }}
                onClick={handleExport}
              >
                Export
              </Button>
            )}
          </div>
        </div>
      </form>
    </FilterLayout>
  );
};

export default Filter;
